import React, { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { components } from 'react-select/lib/components';
import { useDebounce } from 'use-hooks';
import { SelectDropdownIndicator, AutocompleteControlComponent } from './Autocomplete';
import { SelectMenuPortal, defaultStyles } from './BasicSelect';
export const ReactSelectComponents = components;
const DefaultOptionComponent = (props) => React.createElement(ReactSelectComponents.Option, Object.assign({}, props));
const tickerFromOption = (option) => {
    if (option == null) {
        return '';
    }
    const data = option.data;
    if (data != null && typeof data.symbol === 'string' && data.symbol.length > 0) {
        return data.symbol;
    }
    if (option.value != null && String(option.value).length > 0) {
        return String(option.value);
    }
    return option.label != null && option.label !== '' ? String(option.label) : '';
};
const LoadingResults = ({ noResultsFound }) => {
    return useMemo(() => {
        if (noResultsFound) {
            return React.createElement("div", { className: `option select__option` }, "Symbol not found");
        }
        return (React.createElement("div", { className: `option select__option` }, "Loading...") //TODO: add in loading icon when component memos correctly <i className="fas fa-circle-notch fa-spin mx-2" />
        );
    }, [noResultsFound]);
};
export const AsyncAutocomplete = props => {
    const { OptionComponent, defaultValue, dropdownIcon, placeholder, search, onSelect, debounceTimeout, minimumLength, initialValues, isClearable, clearOptionsOnSelect, value, name, isSearchable, onClear, symbol, onBlur, charLimit, rest, rootPortalId, closeMenuOnSelect, blurInputOnSelect, openMenuOnFocus, controlShouldRenderValue, backspaceRemovesValue, commitSymbolToSearchOnBlur, clearOnlyWhenInputMatchesSymbol, } = props;
    const Option = useMemo(() => OptionComponent || DefaultOptionComponent, [OptionComponent]);
    const selectRef = useRef();
    const selectOptionsRef = useRef([]);
    const inputValueRef = useRef('');
    const keepDraftUntilBlurRef = useRef(false);
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [queryValue, setQueryValue] = useState(undefined);
    const [inputValue, setInputValue] = useState('');
    const [minLength] = useState(_.isNumber(minimumLength) ? minimumLength : 2);
    const initialSelectOpts = useMemo(() => {
        return _(initialValues || [])
            .union([value])
            .filter(v => v !== undefined)
            .uniqBy(v => v.value)
            .value();
    }, [value, initialValues]);
    const [selectOptions, setOptions] = useState(initialSelectOpts);
    selectOptionsRef.current = selectOptions;
    inputValueRef.current = inputValue;
    const setSelectOptions = useCallback((values) => {
        return setOptions(values);
    }, []);
    const debouncedQueryValue = useDebounce(queryValue, debounceTimeout || 175);
    useEffect(() => {
        setSelectOptions(initialValues);
    }, [initialValues]);
    useEffect(() => {
        if (debouncedQueryValue && debouncedQueryValue.length >= minLength) {
            search(debouncedQueryValue).then(res => {
                setSelectOptions(res);
                if (res.length <= 0) {
                    setNoResultsFound(true);
                }
            });
        }
    }, [debouncedQueryValue]);
    const onChange = useCallback((val, { action }) => {
        onSelect(val);
        if (commitSymbolToSearchOnBlur) {
            if (action === 'clear' || val == null) {
                keepDraftUntilBlurRef.current = false;
                setInputValue('');
            }
            else if (action === 'select-option' || action === 'set-value') {
                keepDraftUntilBlurRef.current = true;
            }
            else {
                keepDraftUntilBlurRef.current = false;
                if (val != null) {
                    setInputValue(tickerFromOption(val));
                }
            }
        }
        else if (action === 'clear' || val == null) {
            setInputValue('');
        }
        else {
            // Static dropdowns (strike, expiration, etc.): keep input empty so the selected `value`
            // drives the label; copying the label into inputValue makes react-select filter options
            // down to one row and can blank the control (qa always cleared after select).
            setInputValue('');
        }
        const clearOptions = clearOptionsOnSelect && action === 'select-option';
        if (clearOptions ||
            action === 'clear' ||
            action === 'deselect-option' ||
            action === 'remove-value' ||
            action === 'pop-value') {
            setSelectOptions(initialValues || []);
        }
        if (action === 'clear' && onClear) {
            onClear();
        }
    }, [onSelect, onClear, initialValues, clearOptionsOnSelect, setSelectOptions, commitSymbolToSearchOnBlur]);
    const onInputChange = useCallback((val, { action }) => {
        switch (action) {
            case 'input-change': {
                setInputValue(val);
                setSelectOptions(initialValues || []);
                setQueryValue(val);
                setNoResultsFound(false);
                return undefined;
            }
            case 'set-value': {
                if (commitSymbolToSearchOnBlur) {
                    return undefined;
                }
                setInputValue('');
                return undefined;
            }
            case 'menu-close': {
                if (commitSymbolToSearchOnBlur) {
                    return undefined;
                }
                const opts = selectOptionsRef.current;
                const draft = inputValueRef.current;
                if ((!opts || opts.length === 0) && !_.isEmpty(draft)) {
                    setInputValue('');
                    setSelectOptions(initialValues || []);
                }
                return undefined;
            }
            default:
                return undefined;
        }
    }, [initialValues, setSelectOptions, commitSymbolToSearchOnBlur]);
    const getOptionValue = useCallback((option) => {
        const optionVal = _.join([_.get(option, 'label'), _.get(option, 'searchValue'), _.get(option, 'value')], ' ');
        return optionVal;
    }, []);
    const currentValue = useMemo(() => {
        if (_.isNil(value) && !_.isNil(defaultValue)) {
            const initValue = _.find(selectOptions, o => o.data === defaultValue.data);
            return initValue;
        }
        return value || null;
    }, [defaultValue, value, selectOptions]);
    const formatOptionLabel = useCallback((option, labelMeta) => {
        if (!_.isNil(defaultValue) &&
            _.isNil(value) &&
            currentValue &&
            currentValue.value === option.value &&
            labelMeta.context === 'value') {
            return React.createElement("div", { className: `badge select__placeholder` }, placeholder);
        }
        return option.label;
    }, [currentValue, defaultValue, placeholder, value]);
    const styles = useMemo(() => {
        return {
            menuPortal: defaultStyles.menuPortal,
        };
    }, [window.scrollY, defaultStyles.menuPortal]);
    useEffect(() => {
        if (commitSymbolToSearchOnBlur && keepDraftUntilBlurRef.current) {
            return;
        }
        // Only ticker/search callers pass `symbol` (e.g. HttpAutocomplete). Dropdowns like order-leg expiration
        // omit it entirely — previously `!symbol` cleared input every render and hid the selected label (TBFE-2305).
        if (symbol === undefined) {
            return;
        }
        if (!symbol) {
            setInputValue('');
            return;
        }
        setInputValue(symbol);
        setSelectOptions(initialValues || []);
    }, [symbol, commitSymbolToSearchOnBlur, initialValues]);
    const handleBlur = useCallback((_e) => {
        if (commitSymbolToSearchOnBlur) {
            const hadPendingSelection = keepDraftUntilBlurRef.current;
            keepDraftUntilBlurRef.current = false;
            if (hadPendingSelection && symbol) {
                setInputValue(symbol);
            }
        }
        if (onBlur) {
            onBlur();
        }
    }, [commitSymbolToSearchOnBlur, symbol, onBlur]);
    const inputFormat = (iv) => {
        if (charLimit) {
            return iv.slice(0, charLimit);
        }
        return iv;
    };
    const committedTickerForClear = symbol && symbol.length > 0
        ? symbol
        : currentValue
            ? tickerFromOption(currentValue)
            : '';
    const formattedInput = inputFormat(inputValue);
    const inputSyncedForClear = committedTickerForClear.length === 0 ||
        formattedInput.trim() === '' ||
        formattedInput.trim().toUpperCase() === committedTickerForClear.toUpperCase();
    const baseIsClearable = _.isBoolean(isClearable) ? isClearable : true;
    const effectiveIsClearable = clearOnlyWhenInputMatchesSymbol
        ? Boolean(baseIsClearable && currentValue && inputSyncedForClear)
        : baseIsClearable;
    const searchable = _.isBoolean(isSearchable) ? isSearchable : true;
    return (React.createElement(Select, Object.assign({}, (rest || {}), { ref: selectRef, placeholder: placeholder, value: currentValue, inputValue: formattedInput, onInputChange: onInputChange, menuPlacement: 'auto', formatOptionLabel: formatOptionLabel, onChange: onChange, name: name, isClearable: effectiveIsClearable, isSearchable: searchable, filterOption: searchable ? undefined : () => true, options: selectOptions, className: "basic-multi-select", getOptionValue: getOptionValue, classNamePrefix: "select", menuShouldBlockScroll: 'true', styles: styles, menuPortalTarget: document.getElementById(rootPortalId ? rootPortalId : 'portal-root'), noOptionsMessage: e => (e.inputValue ? '' : null), onKeyDown: e => {
            if (e.key === 'Enter') {
                search(inputValue, 1).then(res => {
                    if (res.length > 0 && res[0].value && res[0].value.toUpperCase() === inputValue.toUpperCase()) {
                        onChange(res[0], { action: 'set-value' });
                        // Symbol picker (`commitSymbolToSearchOnBlur`): keep focus so the user can immediately edit/replace the ticker.
                        if (!commitSymbolToSearchOnBlur && selectRef.current) {
                            selectRef.current.blur();
                        }
                    }
                });
            }
        }, components: {
            DropdownIndicator: rootPortalId ? undefined : SelectDropdownIndicator(dropdownIcon || 'fa-arrow-right'),
            Control: AutocompleteControlComponent,
            MenuPortal: SelectMenuPortal,
            Option,
            NoOptionsMessage: () => {
                return React.createElement(LoadingResults, { noResultsFound: noResultsFound });
            },
        }, onBlur: handleBlur }, (closeMenuOnSelect !== undefined ? { closeMenuOnSelect } : {}), (blurInputOnSelect !== undefined ? { blurInputOnSelect } : {}), (openMenuOnFocus !== undefined ? { openMenuOnFocus } : {}), (controlShouldRenderValue !== undefined ? { controlShouldRenderValue } : {}), (backspaceRemovesValue !== undefined ? { backspaceRemovesValue } : {}))));
};
//# sourceMappingURL=AsyncAutocomplete.js.map